import type { JsonObject, JsonValue } from '../api/types';

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** JSON text with object keys sorted, so equal values give equal text. */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      if (value[key] !== undefined) result[key] = sortKeys(value[key]);
    }
    return result;
  }
  return value;
}

export function deepEqual(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

export function clone<T>(value: T): T {
  return structuredClone(value);
}

/** Removes keys whose value is undefined, an empty string, or an empty object (recursively). */
export function compactObject(value: JsonObject): JsonObject {
  const result: JsonObject = {};
  for (const [key, item] of Object.entries(value)) {
    if (item === undefined || item === '') continue;
    if (isPlainObject(item)) {
      const inner = compactObject(item as JsonObject);
      if (Object.keys(inner).length > 0) result[key] = inner;
      continue;
    }
    result[key] = item as JsonValue;
  }
  return result;
}
